/* **Buddy's mark: the MoveBuddy mark cut from a matte pearl that stands still.**
 *
 * The sibling of `brandMark.module.css`, and built the same way: the brand asset
 * (`public/brand/mark.png`, the sharp 384 px mark) as a MASK, never a drawing of it. Canon:
 * `docs/design/ai-buddy-2026-09-30/` boards N and O for the pearl, and the P5 boards (owner,
 * 4 November 2026) for how it behaves. Every layer is cut by the same mask:
 *
 *   · ghost      the mark's own silhouette in the ink at a tenth, so its shape reads at any angle
 *   · ink        the pearl, a conic fill at a fixed angle
 *   · highlight  a fixed spot of light, so the fill reads as a surface
 *
 * ── At rest the mark stands still; while Buddy works only its light breathes ────────────────
 * The earlier build turned the cone every 9 s at rest and lifted the whole mark like a repetition
 * while Buddy worked. The P5 boards keep the pearl and drop the motion: at rest nothing moves, and
 * while Buddy works the mark dims to 40% and back (board P5d-3, `.breathe`). No turn, no lift.
 *
 * ── The hero figure's light (board P5e, variant D „Światło") ────────────────────────────────
 * On the Buddy page's start the mark is the hero figure, 88 px. Its shape never moves; only the
 * pearl's light does, three more layers over the ink:
 *
 *   · pulse      the waiting pearl brightening and dimming once (3.6 s), every 6 to 8 s
 *   · lit        the pearl lit up while the composer has focus
 *   · sweep      one band of light from the feet to the head when the composer takes focus
 *
 * and the cone's angle, which turns after the caret while the trainer types and returns a second
 * after. The pulse and the sweep play when their layer MOUNTS: the caller keys them by a counter,
 * so each new pulse or sweep is a new layer and nothing is restarted by hand. The timing lives in
 * `features/ai-chat` (`model/heroLight.ts`, `lib/useHeroLight.ts`); this file only draws it.
 *
 * ── Reduced motion ───────────────────────────────────────────────────────────────────────────
 * `tokens/base.css` collapses every animation to one instant iteration, so every keyframe below
 * ends on the still picture: the pulse and the sweep at opacity 0. The caller also stops driving
 * the light, and the block at the end holds it dark and square, so a still viewer sees the plain
 * pearl and nothing else.
 *
 * The size is the surface's: a second class sets `width` and `height`. */

/* The cone's start angle. Inherited, so the root carries it for the ink and the lit layer, and
 * registered, so it transitions: the bright side turns after the caret rather than jumping. */
@property --pearl-ang {
  syntax: "<angle>";
  inherits: true;
  initial-value: 0deg;
}

.pearl {
  position: relative;
  display: inline-block;
  flex: none;
  transition-duration: var(--dur-buddy-light-turn);
  transition-property: --pearl-ang;
  transition-timing-function: var(--ease);
}

.layer {
  position: absolute;
  inset: var(--space-0);
  mask-image: url("/brand/mark.png");
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: contain;
}

.ghost {
  background: var(--text);
  opacity: 0.1;
}

.ink {
  background-image: conic-gradient(
    from var(--pearl-ang) at 50% 45%,
    var(--buddy-pearl-a),
    var(--buddy-pearl-b),
    var(--buddy-pearl-c),
    var(--buddy-pearl-b),
    var(--buddy-pearl-a)
  );
}

.highlight {
  background: var(--buddy-pearl-highlight);
}

/* Buddy works: the light breathes, the mark stays where it is. */
.work {
  animation-name: pearl-breathe;
  animation-duration: var(--dur-buddy-work);
  animation-timing-function: var(--ease-buddy-light);
  animation-iteration-count: infinite;
}

/* The light layers: the pearl's bright half, without its dark stop. */
.pulse,
.lit {
  background-image: conic-gradient(
    from var(--pearl-ang) at 50% 45%,
    var(--buddy-pearl-a),
    var(--buddy-pearl-b) 50%,
    var(--buddy-pearl-a)
  );
  opacity: 0;
}

.pulse {
  animation-name: pearl-pulse;
  animation-duration: var(--dur-buddy-light-pulse);
  animation-timing-function: var(--ease-buddy-light);
}

.lit {
  transition-duration: var(--dur-buddy-light-on);
  transition-property: opacity;
  transition-timing-function: var(--ease);
}

.lit.on {
  opacity: 1;
}

.sweep {
  background-image: var(--buddy-pearl-sweep);
  background-repeat: no-repeat;
  background-position: 100% 0;
  background-size: 300% 100%;
  opacity: 0;
  animation-name: pearl-sweep;
  animation-duration: var(--dur-buddy-light-sweep);
  animation-timing-function: var(--ease-buddy-light);
}

@keyframes pearl-breathe {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.4;
  }
}

@keyframes pearl-pulse {
  0%,
  100% {
    opacity: 0;
  }

  45% {
    opacity: 0.85;
  }
}

@keyframes pearl-sweep {
  from {
    background-position: 100% 0;
    opacity: 1;
  }

  to {
    background-position: 0% 0;
    opacity: 1;
  }
}

/* Completely still: no lit pearl, whatever the caller last asked for. (The cone's angle is the
 * caller's to hold square: it stops turning it under reduced motion.) */
@media (prefers-reduced-motion: reduce) {
  .lit.on {
    opacity: 0;
  }
}
